import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";

/**
 * EnhancedTooltip - Modern tooltip with rich content and keyboard shortcuts
 * Features:
 * - Configurable delay
 * - Rich content support (title + description + keyboard shortcut)
 * - Proper positioning
 * - Accessible (role="tooltip")
 */
const EnhancedTooltip = ({
  children,
  content,
  description,
  shortcut,
  position = "top", // top, bottom, left, right
  delay = 500, // ms
  disabled = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const timeoutRef = useRef(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  const showTooltip = () => {
    if (disabled || !content) return;
    
    timeoutRef.current = setTimeout(() => {
      setVisible(true);
      calculatePosition();
    }, delay);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setVisible(false);
  };

  const handleClick = () => {
    hideTooltip();
  };

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const gap = 8; // pixels between trigger and tooltip

    let top = 0;
    let left = 0;

    switch (position) {
      case "top":
        top = triggerRect.top - tooltipRect.height - gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "bottom":
        top = triggerRect.bottom + gap;
        left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
        break;
      case "left":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.left - tooltipRect.width - gap;
        break;
      case "right":
        top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
        left = triggerRect.right + gap;
        break;
      default:
        break;
    }

    // Keep tooltip within viewport
    const padding = 10;
    if (left < padding) left = padding;
    if (left + tooltipRect.width > window.innerWidth - padding) {
      left = window.innerWidth - tooltipRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + tooltipRect.height > window.innerHeight - padding) {
      top = window.innerHeight - tooltipRect.height - padding;
    }

    setCoords({ top, left });
  };

  // Recalculate position when visible
  useEffect(() => {
    if (visible) {
      calculatePosition();
    }
  }, [visible]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle window resize
  useEffect(() => {
    if (!visible) return;

    const handleResize = () => calculatePosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [visible]);

  const hasRichContent = description || shortcut;

  // Arrow styles based on position
  const getArrowStyles = () => {
    const baseStyle = {
      position: 'absolute',
      width: 0,
      height: 0,
      borderStyle: 'solid'
    };

    switch (position) {
      case "top":
        return {
          ...baseStyle,
          bottom: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '6px 6px 0 6px',
          borderColor: '#1f2937 transparent transparent transparent'
        };
      case "bottom":
        return {
          ...baseStyle,
          top: '-6px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderWidth: '0 6px 6px 6px',
          borderColor: 'transparent transparent #1f2937 transparent'
        };
      case "left":
        return {
          ...baseStyle,
          right: '-6px',
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: '6px 0 6px 6px',
          borderColor: 'transparent transparent transparent #1f2937'
        };
      case "right":
        return {
          ...baseStyle,
          left: '-6px',
          top: '50%',
          transform: 'translateY(-50%)',
          borderWidth: '6px 6px 6px 0',
          borderColor: 'transparent #1f2937 transparent transparent'
        };
      default:
        return baseStyle;
    }
  };

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        onClick={handleClick}
        onMouseDown={hideTooltip}
        style={{ display: "inline-block", width: "100%" }}
        aria-describedby={visible ? "enhanced-tooltip" : undefined}
      >
        {children}
      </div>

      {visible && (
        <div
          ref={tooltipRef}
          id="enhanced-tooltip"
          role="tooltip"
          className={`enhanced-tooltip enhanced-tooltip--${position}`}
          style={{
            position: "fixed",
            top: `${coords.top}px`,
            left: `${coords.left}px`,
            zIndex: 10000,
            pointerEvents: 'none',
            animation: 'tooltipFadeIn 0.15s ease'
          }}
        >
          <div 
            className="enhanced-tooltip-content"
            style={{
              background: '#1f2937',
              color: '#fff',
              padding: hasRichContent ? '10px 14px' : '8px 12px',
              borderRadius: '6px',
              fontSize: '13px',
              lineHeight: 1.4,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              maxWidth: '280px'
            }}
          >
            {hasRichContent ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ fontWeight: 600, fontSize: '13px' }}>{content}</div>
                {description && (
                  <div style={{ fontSize: '12px', color: '#d1d5db', lineHeight: 1.5 }}>
                    {description}
                  </div>
                )}
                {shortcut && (
                  <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                    {Array.isArray(shortcut) ? (
                      shortcut.map((key, i) => (
                        <kbd 
                          key={i}
                          style={{
                            padding: '2px 6px',
                            background: 'rgba(255, 255, 255, 0.15)',
                            border: '1px solid rgba(255, 255, 255, 0.25)',
                            borderRadius: '3px',
                            fontSize: '11px',
                            fontFamily: 'monospace',
                            fontWeight: 600
                          }}
                        >
                          {key}
                        </kbd>
                      ))
                    ) : (
                      <kbd 
                        style={{
                          padding: '2px 6px',
                          background: 'rgba(255, 255, 255, 0.15)',
                          border: '1px solid rgba(255, 255, 255, 0.25)',
                          borderRadius: '3px',
                          fontSize: '11px',
                          fontFamily: 'monospace',
                          fontWeight: 600
                        }}
                      >
                        {shortcut}
                      </kbd>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <span style={{ whiteSpace: 'nowrap' }}>{content}</span>
            )}
          </div>
          <div style={getArrowStyles()} />
        </div>
      )}
    </>
  );
};

EnhancedTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  content: PropTypes.node,
  description: PropTypes.string,
  shortcut: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  position: PropTypes.oneOf(["top", "bottom", "left", "right"]),
  delay: PropTypes.number,
  disabled: PropTypes.bool,
};

export default EnhancedTooltip;
